import { createEffect, createSignal, Match, Show, Switch } from "solid-js";
import { defaultCoin, donationIcon } from "~/assets/assets";
import { useClassicContext } from "~/data/classic/classic_context";
import { config } from "~/data/config";
import { HubbleBrandProduct } from "~/data/products";
import {
  HomepageHorizontalSectionBrandCard,
  HorizontalSectionResourceMap,
} from "~/server/types/brand";
import { classNames } from "~/utils/etc";
import HubbleImage from "~/widgets/hubble_image";
import { PhosphorIcon } from "~/widgets/icons";
import { openUnavailableBrandModal, openVoucherL2 } from "../screens";
import styles from "~/widgets/hubble_image.module.scss";

export default function HorizontalSection(props: {
  horizontalSectionsResourceMap: HorizontalSectionResourceMap;
}) {
  const horizontalSectionsResourceMap = props.horizontalSectionsResourceMap;

  const [resource, resourceActions] = horizontalSectionsResourceMap.resource;

  const [isSectionVisible, setIsSectionVisible] = createSignal(true);

  createEffect(() => {
    // hide the section if no brands are available
    if (resource.state === "ready" && resource.latest?.length === 0) {
      setIsSectionVisible(false);
    }
  });

  return (
    <div
      classList={{
        hidden: !isSectionVisible(),
      }}
      class="flex flex-col justify-center py-8 text-white"
      style={{
        "background-color": `#${horizontalSectionsResourceMap.separator.color_code}`,
      }}
    >
      <Show when={resource.state == "ready"} fallback={<ShimmerFallback />}>
        <div class="flex flex-row items-start justify-start">
          <span class="inline-block max-w-[80%] pl-5 pr-2 text-h4">
            {horizontalSectionsResourceMap.separator.heading}
          </span>
          <img
            src={horizontalSectionsResourceMap.separator.image}
            class="h-5 pt-1"
            alt=""
          />
        </div>
        <div class="mt-6"></div>
        <div class="no-scrollbar flex flex-row gap-6 overflow-x-scroll px-5">
          {resource.latest?.map((product) => <BrandCard product={product} />)}
        </div>
      </Show>
    </div>
  );
}

function ShimmerFallback() {
  return (
    <div>
      <div class="px-5">
        <div class={`h-8 w-1/2 rounded-lg ${styles.skeleton}`} />
      </div>
      <div class="mt-6"></div>
      <div class="no-scrollbar flex flex-row gap-6 overflow-x-scroll px-5">
        {[...Array(5)].map(() => (
          <div class="flex w-[104px] flex-shrink-0 flex-col items-start">
            <div class={`h-[116px] w-full rounded-xl ${styles.skeleton}`} />
            <div class="mt-2"></div>
            <div class="flex w-full flex-col items-start px-1 leading-[140%]">
              <div class={`mb-1 h-4 w-10/12 rounded-md ${styles.skeleton}`} />
              <div class={`h-3 w-8/12 rounded-md ${styles.skeleton}`} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function BrandCard({
  product,
}: {
  product: HomepageHorizontalSectionBrandCard;
}) {
  const isCoinReward = product.rewardType === "POSTPAID";
  const { data, isClientDonationType } = useClassicContext();

  const clientName = data.isLoggedIn && data.clientConfig.partnerName;

  const context = useClassicContext();
  const contextData = context.data;
  const clientConfig = contextData.isLoggedIn ? contextData.clientConfig : null;

  return (
    <div
      class="flex w-[104px] cursor-pointer flex-col items-start"
      onClick={() => {
        if (product.status == "INACTIVE") {
          return;
        }
        if (product.brandKey.length > 0) {
          openVoucherL2({
            productId: product.brandKey,
          });
        }
      }}
    >
      <HubbleImage
        src={product.iconImageUrl}
        alt={product.title}
        class={`h-[116px] w-[104px]  ${product.status == "INACTIVE" ? "opacity-50" : ""}`}
      />
      <div class="mt-2"></div>
      <div class="flex flex-col items-start px-1 leading-[140%]">
        <span
          class={classNames(
            "title-section text-subtitleSmall ",
            `${product.status == "INACTIVE" ? "opacity-50" : ""}`
          )}
        >
          {product.title}
        </span>
        <span class="body-small  w-full text-f12 text-textNormal">
          {product.category}
        </span>
        <Switch>
          <Match when={clientConfig?.payoutsEnabled}>
            <div class=" flex cursor-pointer items-center gap-1">
              <p class="text-normal font-bold uppercase ">Get</p>
              <PhosphorIcon
                name="caret-right"
                class="!text-[12px] font-bold "
                size="bold"
              />
            </div>
          </Match>
          <Match when={product.discountPercentage == 0}>
            <span class="flex items-center gap-1"></span>
          </Match>
          <Match when={isClientDonationType()}>
            <span class=" flex text-bolder text-successDark">
              <HubbleImage src={donationIcon} alt="" class="h-5 w-5" />
              <span class="mx-1">{" Donate "} </span>
              {product.discountPercentage}
              <span class=" text-bolder uppercase">%</span>
            </span>
          </Match>

          <Match when={isCoinReward}>
            <span class="flex items-center gap-1">
              <img
                class="h-6 w-6"
                src={
                  data.isLoggedIn && data.clientConfig.coinIconUrl
                    ? data.clientConfig.coinIconUrl
                    : defaultCoin
                }
                alt="coin"
              />
              <span class="text-h3 text-coinPercentageOrange">
                {product.discountPercentage}
                <span class="self-end text-bolder uppercase">%</span>
              </span>
            </span>
          </Match>
          <Match when={!isCoinReward}>
            <span class="title-header !text-[19px] text-h4  text-successDark">
              {product.discountPercentage}
              <span
                class="title-appbar text-bolder"
                classList={{
                  lowercase:
                    config.customizedSDKClients.lowercaseTitleText.includes(
                      clientName || ""
                    ),
                  uppercase:
                    !config.customizedSDKClients.lowercaseTitleText.includes(
                      clientName || ""
                    ),
                }}
              >
                % off
              </span>
            </span>
          </Match>
        </Switch>
      </div>
    </div>
  );
}
